// packages/app/src/components/admin/models/ArInsumosConfig.tsx
// AGC: [AR][INSUMOS] Admin editor for the Argentine inputs catalog (config/ar_insumos).
// AGC: Faithful UX rebuild — mirrors Brazil's InputsMasterConfig (stats, search/filter, table, modal,
// AGC: add/edit/delete, CSV export) but reads/writes the AR catalog. Separate from Brazil's
// AGC: config/inputsMaster — country separation by document. Brazil's InputsMasterConfig is untouched.
// AGC: Additive fields (costPerUnit/activeIngredient/applicationMethod/safetyIntervalDays) are optional
// AGC: and ignored by the generator (which only reads inputId/qty/unit) — they enrich the catalog only.
import React, { useEffect, useState, useCallback } from 'react'
import { useTranslation } from '../../../hooks'
import { MetricCard } from './MetricCard'
import { SectionCard } from './SectionCard'
import { getBackendUrl } from '../../../utils/backendUrl'
import { useAuth } from '../../../hooks/useAuth'
import {
  Plus,
  Search,
  Edit2,
  Trash2,
  Save,
  X,
  RefreshCw,
  Download,
  Package,
  AlertTriangle,
  Check,
} from 'lucide-react'

// AGC: AR input shape (config/ar_insumos.inputs[id]). Additive fields are optional.
interface ArInput {
  id: string
  name: string
  category: string
  unit: string
  defaultQuantityPerHa?: number
  costPerUnit?: number
  activeIngredient?: string
  applicationMethod?: string
  // AGC: null (not undefined) so clearing it persists — the PUT deep-merges and undefined is dropped.
  safetyIntervalDays?: number | null
  source?: string
  country?: string
  isActive?: boolean
}

// AGC: Canonical AR input categories (from bootstrapArInsumos). Labels via admin.arInsumos.cat.*.
const AR_CATEGORIES = ['seed', 'inoculant', 'fertilizer', 'herbicide', 'fungicide', 'insecticide']
const CAT_COLOR: Record<string, string> = {
  seed: '#22C55E',
  inoculant: '#14B8A6',
  fertilizer: '#3B82F6',
  herbicide: '#F59E0B',
  fungicide: '#8B5CF6',
  insecticide: '#EF4444',
}
const CAT_ICON: Record<string, string> = {
  seed: '🌱',
  inoculant: '🦠',
  fertilizer: '🧪',
  herbicide: '🧴',
  fungicide: '🍄',
  insecticide: '🐛',
}
// AGC: AR-relevant units (raw symbols/words — not translated). Non-listed values are preserved on edit.
const AR_UNITS = ['kg', 'L', 'g', 'mL', 'dosis', 'semillas', 'unidades', 't']
// AGC: Application methods mirror Brazil; canonical value -> i18n label key.
const APP_METHODS: Array<{ value: string; key: string }> = [
  { value: 'spray', key: 'spray' },
  { value: 'soil', key: 'soil' },
  { value: 'seed_treatment', key: 'seedTreatment' },
  { value: 'foliar', key: 'foliar' },
  { value: 'planting', key: 'planting' },
]

export const ArInsumosConfig: React.FC = () => {
  const { t } = useTranslation()
  const { getAuthToken } = useAuth()
  // AGC: build auth headers per request with a fresh Firebase ID token (admin gate)
  const authHeaders = useCallback(async (): Promise<HeadersInit> => {
    const token = await getAuthToken()
    return { Authorization: `Bearer ${token ?? ''}`, 'Content-Type': 'application/json' }
  }, [getAuthToken])
  const [inputs, setInputs] = useState<ArInput[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCategory, setSelectedCategory] = useState<string>('all')
  const [showInactive, setShowInactive] = useState(false)

  // AGC: Modal state — editingId null means "new input".
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [form, setForm] = useState<Partial<ArInput>>({})

  const catLabel = (cat: string) =>
    AR_CATEGORIES.includes(cat) ? t(`admin.arInsumos.cat.${cat}`) : cat

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await fetch(`${getBackendUrl()}/api/v1/admin/config/ar-insumos`, { headers: await authHeaders() })
      if (res.status === 401 || res.status === 403) {
        setError(t(res.status === 403 ? 'admin.auth.forbidden' : 'admin.auth.notAuthenticated'))
        return
      }
      const data = await res.json()
      if (data.success && Array.isArray(data.data?.inputs)) {
        setInputs((data.data.inputs as ArInput[]).sort((a, b) => a.name.localeCompare(b.name)))
      } else {
        setError(t('admin.arInsumos.loadError'))
      }
    } catch {
      setError(t('admin.arInsumos.loadError'))
    } finally {
      setLoading(false)
    }
  }, [t, authHeaders])

  useEffect(() => {
    load()
  }, [load])

  useEffect(() => {
    if (!success) return
    const timer = setTimeout(() => setSuccess(null), 3000)
    return () => clearTimeout(timer)
  }, [success])

  const filtered = inputs.filter((input) => {
    const term = searchTerm.toLowerCase()
    const matchesSearch =
      term === '' ||
      input.name.toLowerCase().includes(term) ||
      (input.activeIngredient?.toLowerCase().includes(term) ?? false)
    const matchesCategory = selectedCategory === 'all' || input.category === selectedCategory
    const matchesActive = showInactive || input.isActive !== false
    return matchesSearch && matchesCategory && matchesActive
  })

  const stats = {
    total: inputs.length,
    active: inputs.filter((i) => i.isActive !== false).length,
    categories: new Set(inputs.map((i) => i.category)).size,
    inactive: inputs.filter((i) => i.isActive === false).length,
  }

  const openNew = () => {
    setEditingId(null)
    setForm({ name: '', category: 'herbicide', unit: 'L', defaultQuantityPerHa: 1, costPerUnit: 0, isActive: true })
    setIsModalOpen(true)
  }

  const openEdit = (input: ArInput) => {
    setEditingId(input.id)
    setForm({ ...input })
    setIsModalOpen(true)
  }

  const closeModal = () => {
    setIsModalOpen(false)
    setEditingId(null)
    setForm({})
  }

  // AGC: Build a stable id for a new input from its name; suffix on collision.
  const buildId = (name: string) => {
    const slug = name
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
    const base = `ar_${slug || 'input'}`
    const taken = new Set(inputs.map((i) => i.id))
    if (!taken.has(base)) return base
    return `${base}_${Date.now().toString(36)}`
  }

  const handleSave = async () => {
    if (!form.name?.trim() || !form.category || !form.unit) {
      setError(t('admin.arInsumos.fillRequired'))
      return
    }
    const id = editingId || buildId(form.name)
    // AGC: Carry ALL fields through (data-conversion safety) — additive fields included.
    const payload: ArInput = {
      id,
      name: form.name.trim(),
      category: form.category,
      unit: form.unit,
      defaultQuantityPerHa: form.defaultQuantityPerHa ?? 0,
      costPerUnit: form.costPerUnit ?? 0,
      activeIngredient: form.activeIngredient?.trim() || '',
      applicationMethod: form.applicationMethod || '',
      // AGC: send null (not undefined) so a cleared value overwrites the stored one under merge:true.
      safetyIntervalDays: form.safetyIntervalDays ?? null,
      source: form.source?.trim() || '',
      country: 'AR',
      isActive: form.isActive !== false,
    }
    setSaving(true)
    setError(null)
    try {
      const res = await fetch(`${getBackendUrl()}/api/v1/admin/config/ar-insumos/${id}`, {
        method: 'PUT',
        headers: await authHeaders(),
        body: JSON.stringify({ input: payload }),
      })
      if (res.status === 401 || res.status === 403) {
        setError(t(res.status === 403 ? 'admin.auth.forbidden' : 'admin.auth.notAuthenticated'))
        return
      }
      const data = await res.json()
      if (!data.success) {
        setError(data.error || t('admin.arInsumos.loadError'))
        return
      }
      setSuccess(t('admin.arInsumos.saved'))
      closeModal()
      load()
    } catch {
      setError(t('admin.arInsumos.loadError'))
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (input: ArInput) => {
    if (!confirm(t('admin.arInsumos.deleteConfirm', { name: input.name }))) return
    setError(null)
    try {
      const res = await fetch(`${getBackendUrl()}/api/v1/admin/config/ar-insumos/${input.id}`, {
        method: 'DELETE',
        headers: await authHeaders(),
      })
      if (res.status === 401 || res.status === 403) {
        setError(t(res.status === 403 ? 'admin.auth.forbidden' : 'admin.auth.notAuthenticated'))
        return
      }
      const data = await res.json()
      if (!data.success) {
        setError(data.error || t('admin.arInsumos.loadError'))
        return
      }
      setSuccess(t('admin.arInsumos.deleted'))
      load()
    } catch {
      setError(t('admin.arInsumos.loadError'))
    }
  }

  // AGC: Export the full catalog to CSV (additive fields included).
  const handleExport = () => {
    const header = ['id', 'name', 'category', 'unit', 'defaultQuantityPerHa', 'costPerUnit', 'activeIngredient', 'applicationMethod', 'safetyIntervalDays', 'source', 'isActive']
    const rows = inputs.map((i) =>
      [
        i.id,
        `"${i.name}"`,
        i.category,
        i.unit,
        i.defaultQuantityPerHa ?? '',
        i.costPerUnit ?? '',
        `"${i.activeIngredient || ''}"`,
        i.applicationMethod || '',
        i.safetyIntervalDays ?? '',
        `"${(i.source || '').replace(/"/g, "'")}"`,
        i.isActive !== false ? 'Yes' : 'No',
      ].join(','),
    )
    const csv = [header.join(','), ...rows].join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'ar-insumos-catalog.csv'
    a.click()
    URL.revokeObjectURL(url)
  }

  if (loading) {
    return (
      <div className="p-6 flex items-center gap-2 text-slate-500">
        <RefreshCw className="w-4 h-4 animate-spin" /> {t('common.loading')}
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* AGC: Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
            <Package className="w-7 h-7 text-emerald-600" />
            🇦🇷 {t('admin.arInsumos.title')}
          </h2>
          <p className="text-slate-600 dark:text-slate-300 mt-1">{t('admin.arInsumos.note')}</p>
        </div>
      </div>

      {/* AGC: Error/Success */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400" />
          <span className="text-red-700 dark:text-red-300">{error}</span>
          <button onClick={() => setError(null)} className="ml-auto" aria-label="dismiss">
            <X className="w-4 h-4 text-red-600" />
          </button>
        </div>
      )}
      {success && (
        <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-lg p-4 flex items-center gap-2">
          <Check className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
          <span className="text-emerald-700 dark:text-emerald-300">{success}</span>
        </div>
      )}

      {/* AGC: Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <MetricCard icon="📦" value={stats.total} label={t('admin.arInsumos.statTotal')} color="#3B82F6" />
        <MetricCard icon="✅" value={stats.active} label={t('admin.arInsumos.statActive')} color="#22C55E" />
        <MetricCard icon="🏷️" value={stats.categories} label={t('admin.arInsumos.statCategories')} color="#8B5CF6" />
        <MetricCard icon="⚠️" value={stats.inactive} label={t('admin.arInsumos.statInactive')} color="#F59E0B" />
      </div>

      <SectionCard title={t('admin.arInsumos.title')} icon="📦">
        {/* AGC: Filters + actions */}
        <div className="flex flex-wrap gap-4 mb-4">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder={t('admin.arInsumos.search')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-slate-200 dark:border-white/10 rounded-lg bg-white dark:bg-admin-bg-dark dark:text-slate-100 text-slate-900"
              />
            </div>
          </div>

          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="px-4 py-2 border border-slate-200 dark:border-white/10 rounded-lg bg-white dark:bg-admin-bg-dark dark:text-slate-100 text-slate-900"
          >
            <option value="all">{t('admin.arInsumos.allCategories')}</option>
            {AR_CATEGORIES.map((cat) => (
              <option key={cat} value={cat}>
                {CAT_ICON[cat]} {catLabel(cat)}
              </option>
            ))}
          </select>

          <label className="flex items-center gap-2 px-4 py-2 border border-slate-200 dark:border-white/10 rounded-lg cursor-pointer">
            <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} className="rounded" />
            <span className="text-sm text-slate-700 dark:text-slate-300">{t('admin.arInsumos.showInactive')}</span>
          </label>

          <button
            onClick={openNew}
            className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            {t('admin.arInsumos.addInput')}
          </button>
          <button
            onClick={load}
            disabled={loading}
            className="px-4 py-2 border border-slate-200 dark:border-white/10 rounded-lg hover:bg-slate-100 dark:hover:bg-white/5 flex items-center gap-2"
            aria-label="refresh"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={handleExport}
            className="px-4 py-2 border border-slate-200 dark:border-white/10 rounded-lg hover:bg-slate-100 dark:hover:bg-white/5 flex items-center gap-2"
          >
            <Download className="w-4 h-4" />
            {t('admin.arInsumos.export')}
          </button>
        </div>

        {/* AGC: Table */}
        {filtered.length === 0 ? (
          <div className="text-center py-10 text-slate-500 dark:text-slate-400">
            <Package className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>{t('admin.arInsumos.noResults')}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200 dark:border-white/5">
                  <th className="text-left py-3 px-4 text-sm font-medium text-slate-500 dark:text-slate-400">{t('admin.arInsumos.name')}</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-slate-500 dark:text-slate-400">{t('admin.arInsumos.category')}</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-slate-500 dark:text-slate-400">{t('admin.arInsumos.unit')}</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-slate-500 dark:text-slate-400">{t('admin.arInsumos.qtyPerHa')}</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-slate-500 dark:text-slate-400">{t('admin.arInsumos.cost')}</th>
                  <th className="text-center py-3 px-4 text-sm font-medium text-slate-500 dark:text-slate-400">{t('admin.arInsumos.status')}</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-slate-500 dark:text-slate-400">{t('admin.arInsumos.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((input) => (
                  <tr
                    key={input.id}
                    className={`border-b border-slate-100 dark:border-white/5 hover:bg-slate-50 dark:hover:bg-white/[0.02] ${
                      input.isActive === false ? 'opacity-50' : ''
                    }`}
                  >
                    <td className="py-3 px-4">
                      <div className="font-medium text-slate-900 dark:text-slate-100">{input.name}</div>
                      {input.activeIngredient && (
                        <div className="text-xs text-slate-500 dark:text-slate-400">{input.activeIngredient}</div>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      <span
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs"
                        style={{ backgroundColor: `${CAT_COLOR[input.category] || '#64748B'}20`, color: CAT_COLOR[input.category] || '#64748B' }}
                      >
                        {CAT_ICON[input.category] || '📦'} {catLabel(input.category)}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-slate-700 dark:text-slate-300">{input.unit}</td>
                    <td className="py-3 px-4 text-right text-slate-700 dark:text-slate-300">{input.defaultQuantityPerHa ?? '—'}</td>
                    <td className="py-3 px-4 text-right text-slate-700 dark:text-slate-300">
                      {input.costPerUnit ? `$ ${input.costPerUnit.toFixed(2)}` : '—'}
                    </td>
                    <td className="py-3 px-4 text-center">
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs ${
                          input.isActive !== false
                            ? 'bg-success-50 dark:bg-emerald-500/15 text-brand-primary dark:text-emerald-400'
                            : 'bg-slate-100 dark:bg-white/[0.04] text-slate-600 dark:text-slate-300'
                        }`}
                      >
                        {input.isActive !== false ? `✓ ${t('admin.arInsumos.active')}` : `✗ ${t('admin.arInsumos.inactive')}`}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button onClick={() => openEdit(input)} className="p-1 hover:bg-slate-200 dark:hover:bg-white/10 rounded" aria-label="edit">
                          <Edit2 className="w-4 h-4 text-slate-600 dark:text-slate-400" />
                        </button>
                        <button onClick={() => handleDelete(input)} className="p-1 hover:bg-red-100 dark:hover:bg-red-900/20 rounded" aria-label="delete">
                          <Trash2 className="w-4 h-4 text-red-600 dark:text-red-400" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>

      {/* AGC: Edit/Create modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[90] p-4">
          <div className="bg-white dark:bg-admin-card-dark rounded-xl shadow-card dark:shadow-none w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-200 dark:border-white/5 flex items-center justify-between">
              <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
                {editingId ? t('admin.arInsumos.editInput') : t('admin.arInsumos.newInput')}
              </h3>
              <button onClick={closeModal} aria-label="close">
                <X className="w-5 h-5 text-slate-500 dark:text-slate-400" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  {t('admin.arInsumos.name')} *
                </label>
                <input
                  type="text"
                  value={form.name || ''}
                  onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                  className="w-full px-4 py-2 border border-slate-200 dark:border-white/10 rounded-lg bg-white dark:bg-admin-bg-dark dark:text-slate-100 text-slate-900"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    {t('admin.arInsumos.category')} *
                  </label>
                  <select
                    value={form.category || 'herbicide'}
                    onChange={(e) => setForm((prev) => ({ ...prev, category: e.target.value }))}
                    className="w-full px-4 py-2 border border-slate-200 dark:border-white/10 rounded-lg bg-white dark:bg-admin-bg-dark dark:text-slate-100 text-slate-900"
                  >
                    {/* AGC: Preserve a non-canonical existing category as its own option. */}
                    {form.category && !AR_CATEGORIES.includes(form.category) && (
                      <option value={form.category}>{form.category}</option>
                    )}
                    {AR_CATEGORIES.map((cat) => (
                      <option key={cat} value={cat}>
                        {CAT_ICON[cat]} {catLabel(cat)}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    {t('admin.arInsumos.unit')} *
                  </label>
                  <select
                    value={form.unit || 'L'}
                    onChange={(e) => setForm((prev) => ({ ...prev, unit: e.target.value }))}
                    className="w-full px-4 py-2 border border-slate-200 dark:border-white/10 rounded-lg bg-white dark:bg-admin-bg-dark dark:text-slate-100 text-slate-900"
                  >
                    {form.unit && !AR_UNITS.includes(form.unit) && <option value={form.unit}>{form.unit}</option>}
                    {AR_UNITS.map((u) => (
                      <option key={u} value={u}>
                        {u}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    {t('admin.arInsumos.qtyPerHa')}
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={form.defaultQuantityPerHa ?? ''}
                    onChange={(e) => setForm((prev) => ({ ...prev, defaultQuantityPerHa: parseFloat(e.target.value) || 0 }))}
                    className="w-full px-4 py-2 border border-slate-200 dark:border-white/10 rounded-lg bg-white dark:bg-admin-bg-dark dark:text-slate-100 text-slate-900"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    {t('admin.arInsumos.cost')}
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={form.costPerUnit ?? ''}
                    onChange={(e) => setForm((prev) => ({ ...prev, costPerUnit: parseFloat(e.target.value) || 0 }))}
                    className="w-full px-4 py-2 border border-slate-200 dark:border-white/10 rounded-lg bg-white dark:bg-admin-bg-dark dark:text-slate-100 text-slate-900"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  {t('admin.arInsumos.activeIngredient')}
                </label>
                <input
                  type="text"
                  value={form.activeIngredient || ''}
                  onChange={(e) => setForm((prev) => ({ ...prev, activeIngredient: e.target.value }))}
                  className="w-full px-4 py-2 border border-slate-200 dark:border-white/10 rounded-lg bg-white dark:bg-admin-bg-dark dark:text-slate-100 text-slate-900"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    {t('admin.arInsumos.appMethodLabel')}
                  </label>
                  <select
                    value={form.applicationMethod || ''}
                    onChange={(e) => setForm((prev) => ({ ...prev, applicationMethod: e.target.value }))}
                    className="w-full px-4 py-2 border border-slate-200 dark:border-white/10 rounded-lg bg-white dark:bg-admin-bg-dark dark:text-slate-100 text-slate-900"
                  >
                    <option value="">{t('admin.arInsumos.appMethodNone')}</option>
                    {APP_METHODS.map((m) => (
                      <option key={m.value} value={m.value}>
                        {t(`admin.arInsumos.appMethod.${m.key}`)}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    {t('admin.arInsumos.safetyInterval')}
                  </label>
                  <input
                    type="number"
                    value={form.safetyIntervalDays ?? ''}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        safetyIntervalDays: e.target.value ? parseInt(e.target.value, 10) : undefined,
                      }))
                    }
                    className="w-full px-4 py-2 border border-slate-200 dark:border-white/10 rounded-lg bg-white dark:bg-admin-bg-dark dark:text-slate-100 text-slate-900"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  {t('admin.arInsumos.source')}
                </label>
                <input
                  type="text"
                  value={form.source || ''}
                  onChange={(e) => setForm((prev) => ({ ...prev, source: e.target.value }))}
                  className="w-full px-4 py-2 border border-slate-200 dark:border-white/10 rounded-lg bg-white dark:bg-admin-bg-dark dark:text-slate-100 text-slate-900"
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="ar-input-active"
                  checked={form.isActive !== false}
                  onChange={(e) => setForm((prev) => ({ ...prev, isActive: e.target.checked }))}
                  className="rounded"
                />
                <label htmlFor="ar-input-active" className="text-sm text-slate-700 dark:text-slate-300">
                  {t('admin.arInsumos.active')}
                </label>
              </div>
            </div>

            <div className="p-6 border-t border-slate-200 dark:border-white/5 flex justify-end gap-3">
              <button
                onClick={closeModal}
                className="px-4 py-2 border border-slate-200 dark:border-white/10 rounded-lg hover:bg-slate-100 dark:hover:bg-white/5"
              >
                {t('admin.arInsumos.cancel')}
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg flex items-center gap-2 disabled:opacity-50"
              >
                {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {editingId ? t('admin.arInsumos.update') : t('admin.arInsumos.create')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ArInsumosConfig
